import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { nuclearn_api_url } from "src/App";
import { PARTS_QUERY_KEY } from "./PartService";

// ===========================
// Types
// ===========================

export interface PartGroup {
  id: number;
  name: string;
  description?: string | null;
  create_user_fk: number;
  visibility_level: string;
  create_date: string;
  part_count: number;
}

export interface CreatePartGroupPayload {
  name: string;
  description?: string | null;
  visibility_level?: string;
}

export interface UpdatePartGroupPayload {
  name?: string;
  description?: string | null;
  visibility_level?: string;
}

// ===========================
// Query Keys
// ===========================

export const PART_GROUPS_QUERY_KEY = "parts-ai.partGroups";
export const PART_GROUP_DETAIL_QUERY_KEY = "parts-ai.partGroupDetail";

// ===========================
// API Functions
// ===========================

// Wrapped in a function so `nuclearn_api_url` is read at call-time, not at
// module-load time — avoids a TDZ ReferenceError if a circular import chain
// reaches this file before App.tsx finishes initializing the export.
const partsBase = () => `${nuclearn_api_url}/products/parts`;

const createPartGroup = async (
  payload: CreatePartGroupPayload,
): Promise<PartGroup> => {
  const response = await axios.post<PartGroup>(
    `${partsBase()}/part-groups`,
    payload,
  );
  return response.data;
};

const listPartGroups = async (): Promise<PartGroup[]> => {
  const response = await axios.get<PartGroup[]>(`${partsBase()}/part-groups`);
  return response.data;
};

const getPartGroup = async (groupId: number): Promise<PartGroup> => {
  const response = await axios.get<PartGroup>(
    `${partsBase()}/part-groups/${groupId}`,
  );
  return response.data;
};

const updatePartGroup = async (
  groupId: number,
  payload: UpdatePartGroupPayload,
): Promise<PartGroup> => {
  const response = await axios.put<PartGroup>(
    `${partsBase()}/part-groups/${groupId}`,
    payload,
  );
  return response.data;
};

const deletePartGroup = async (groupId: number): Promise<void> => {
  await axios.delete(`${partsBase()}/part-groups/${groupId}`);
};

// ===========================
// React Query Hooks
// ===========================

export const useListPartGroups = () => {
  return useQuery([PART_GROUPS_QUERY_KEY], () => listPartGroups(), {
    refetchOnWindowFocus: false,
    staleTime: 30 * 1000,
  });
};

export const useGetPartGroup = (groupId: number | undefined) => {
  return useQuery(
    [PART_GROUP_DETAIL_QUERY_KEY, groupId],
    () => getPartGroup(groupId!),
    {
      enabled: !!groupId,
      refetchOnWindowFocus: false,
    },
  );
};

export const useCreatePartGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    (payload: CreatePartGroupPayload) => createPartGroup(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
      },
    },
  );
};

export const useUpdatePartGroup = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      groupId,
      payload,
    }: {
      groupId: number;
      payload: UpdatePartGroupPayload;
    }) => updatePartGroup(groupId, payload),
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
        queryClient.invalidateQueries([
          PART_GROUP_DETAIL_QUERY_KEY,
          variables.groupId,
        ]);
        // Rename / visibility changes can affect how the part lists render
        // (group name display, filter-dropdown options); refresh so cached
        // PartListItem rows don't drift from the source group.
        queryClient.invalidateQueries([PARTS_QUERY_KEY]);
      },
    },
  );
};

export const useDeletePartGroup = () => {
  const queryClient = useQueryClient();
  return useMutation((groupId: number) => deletePartGroup(groupId), {
    onSuccess: () => {
      queryClient.invalidateQueries([PART_GROUPS_QUERY_KEY]);
      // Parts may still hold a stale `part_group_fk` pointing at the now-
      // deleted group; refresh the list so the dashboard doesn't render
      // references to a group that no longer exists.
      queryClient.invalidateQueries([PARTS_QUERY_KEY]);
    },
  });
};
